import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, VStack, useToast, Flex, Text } from '@chakra-ui/react';
import apiEndpoint from '../config/data';
import {
  Button,
  TextInput,
  Link,
  HeadingText,
  BodyText,
} from '@spotoninc/nexus-react';
import { AccountIcon } from '@spotoninc/nexus-web-assets';
import { EmailIcon } from '@chakra-ui/icons';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { isReCaptchaRequired } from '../../utils/recaptchaUtils';

const QuickRegisterContent = ({
  initialCompanyName = '',
  initialCompanyEmail = '',
  footnoteText,
  loginLinkColor,
}) => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(initialCompanyEmail);
  const [companyName, setCompanyName] = useState(initialCompanyName);
  const [reCapPass, setReCapPass] = useState(false);
  const [software, setSoftware] = useState('');
  const [zip, setZip] = useState('');
  const [blid, setBlid] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const navigate = useNavigate();

  // Parse URL parameters when component mounts
  useEffect(() => {
    // Extract parameters from URL
    const softwareParam = searchParams.get('software');
    const merchantNameParam = searchParams.get('merchantName');
    const zipParam = searchParams.get('zip');
    const blidParam = searchParams.get('blid');
    const emailParam = searchParams.get('email');
    const firstNameParam = searchParams.get('f_name');
    const lastNameParam = searchParams.get('l_name');
    const phoneParam = searchParams.get('phone');

    // Update state with URL parameters if they exist
    if (softwareParam) setSoftware(softwareParam);
    if (merchantNameParam) setCompanyName(merchantNameParam);
    if (zipParam) setZip(zipParam);
    if (blidParam) setBlid(blidParam);
    if (emailParam) setEmail(emailParam);
    if (firstNameParam) setFirstName(firstNameParam);
    if (lastNameParam) setLastName(lastNameParam);
    if (phoneParam) setPhoneNumber(phoneParam);

    // Auto-pass ReCAPTCHA check when not on production or QA environments
    if (!isReCaptchaRequired()) {
      setReCapPass(true);
    }

    // URL parameters are now set in state
  }, [searchParams]);

  const handleRegister = async () => {
    if (!email || !companyName) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Only check ReCAPTCHA on required hosts
    if (isReCaptchaRequired() && !reCapPass) {
      toast({
        title: 'Error',
        description: 'Please complete the ReCAPTCHA verification',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('company_name', companyName);
      formData.append('email', email);

      // Add additional parameters if they exist
      if (software) formData.append('accounting_software', software);
      if (zip) formData.append('zip', zip);
      if (blid && blid.trim() !== '') formData.append('blid', blid.trim());
      if (firstName) formData.append('first_name', firstName);
      if (lastName) formData.append('last_name', lastName);
      if (phoneNumber) formData.append('phone', phoneNumber);

      // Add the brand parameter to sent the right email template
      formData.append('brand', 'profit_assist');

      // Form data is ready to be sent

      await fetch(apiEndpoint + '/api/miniReg/', {
        method: 'POST',
        body: formData,
      });

      // Navigate to the confirmation page instead of showing a toast
      // Pass the email as a URL parameter
      navigate(`/registration-confirmation?email=${encodeURIComponent(email)}`);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'An error occurred during registration',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack w="100%" h="100%" spacing={8} maxWidth="400px">
      <Flex direction="column" align="center" gap={2}>
        <HeadingText variant="heading2" color="neutral.900">
          New Registration
        </HeadingText>
        <Text variant="body2" color="neutral.600">
          Enter your information to get started.
        </Text>
        {/* Software info is captured but not displayed */}
      </Flex>

      <VStack spacing={4} w="auto" minW="70%">
        <TextInput
          placeholder="Company Name"
          value={companyName}
          onChange={e => setCompanyName(e.target.value)}
          leadingIcon={AccountIcon}
          style={{
            width: '100%',
          }}
        />

        <TextInput
          type="email"
          placeholder="Email address"
          value={email}
          onChange={e => setEmail(e.target.value)}
          leadingIcon={EmailIcon}
          style={{
            width: '100%',
          }}
        />

        {/* URL parameters are captured but not displayed */}

        {/* Only show ReCAPTCHA on qa.usestarfish.com or app.usestarfish.com */}
        {isReCaptchaRequired() && (
          <Flex width={'100%'} justifyContent={'center'} mb={4}>
            <ReCAPTCHA
              render="explicit"
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={() => setReCapPass(true)}
              onError={() => {
                setReCapPass(false);
                toast({
                  title: 'Error',
                  description: 'Please try the ReCAPTCHA again!',
                  status: 'error',
                  duration: 3000,
                  isClosable: true,
                });
              }}
              onExpired={() => {
                setReCapPass(false);
                toast({
                  title: 'Warning',
                  description: 'ReCAPTCHA has expired, please verify again',
                  status: 'warning',
                  duration: 3000,
                  isClosable: true,
                });
              }}
              style={{
                transformOrigin: '175px 0',
              }}
            />
          </Flex>
        )}

        <Button
          variant="primary"
          onClick={handleRegister}
          isLoading={loading}
          loadingText="Creating account..."
          fullWidth
          className="centered-spinner-button"
        >
          Continue
        </Button>
        {footnoteText && (
          <Box maxWidth="400px" w="100%">
            <BodyText size="small" color="neutral.600">
              {footnoteText}
            </BodyText>
          </Box>
        )}
        <Text>
          Already registered?{' '}
          <Link
            href="/login"
            variant="primary"
            style={loginLinkColor ? { color: loginLinkColor } : undefined}
          >
            Log in
          </Link>
        </Text>
      </VStack>
    </VStack>
  );
};

QuickRegisterContent.propTypes = {
  initialCompanyName: PropTypes.string,
  initialCompanyEmail: PropTypes.string,
  footnoteText: PropTypes.string,
  loginLinkColor: PropTypes.string,
};

export default QuickRegisterContent;
